// Cave84 — BulkActionBar (Inventory bulk actions)
// Appears when one or more table rows are selected. Before this existed the
// row checkboxes were decorative: nothing consumed the selection.

import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSetBottlesSpecialOccasion } from '@/hooks/useBottles';
import { useCreateWishlistEntries } from '@/hooks/useWishlist';
import type { GroupedInventoryRow } from '@/hooks/useInventory';

const C = {
  surface: '#1E1B17',
  border: '#3A3530',
  gold: '#C49255',
  text: '#ECE6D4',
  textDim: '#B8B0A2',
  textMuted: '#908977',
};

interface Props {
  rows: GroupedInventoryRow[];
  isOwner: boolean;
  isMobile: boolean;
  /** Transient result message shown inside the bar (e.g. "12 bottles moved"). */
  notice: string | null;
  onNotice: (message: string) => void;
  onClear: () => void;
  onTransfer: () => void;
  onExport: () => void;
}

export default function BulkActionBar({
  rows, isOwner, isMobile, notice, onNotice, onClear, onTransfer, onExport,
}: Props) {
  const setSpecial = useSetBottlesSpecialOccasion();
  const addToWishlist = useCreateWishlistEntries();

  const bottles = useMemo(() => rows.flatMap(r => r.bottles), [rows]);
  // Selection that is already entirely flagged toggles the other way, so the
  // button reads as a state switch rather than a one-way stamp.
  const allSpecial = bottles.length > 0 && bottles.every(b => b.is_special_occasion);

  const isBusy = setSpecial.isPending || addToWishlist.isPending;

  const barRef = useRef<HTMLDivElement>(null);
  const [navHeight, setNavHeight] = useState(0);
  const visible = rows.length > 0;

  // Mobile: dock the bar directly on top of the real BottomNav. The offset is
  // measured from the nav element itself (height includes the safe-area
  // padding), so it cannot drift from a hard-coded constant and overlap it.
  useLayoutEffect(() => {
    if (!isMobile || !visible) return;
    const nav = document.querySelector<HTMLElement>('nav[aria-label="Primary"]');
    if (!nav) return;
    const update = () => setNavHeight(nav.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(nav);
    return () => ro.disconnect();
  }, [isMobile, visible]);

  // Publish the bar's height as --bulk-bar-h so AppShell can pad <main> (the
  // last rows and pagination stay reachable) and the Add-Bottle FAB can lift
  // above the bar instead of being half-covered by it.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = barRef.current;
    if (!visible || !el) {
      root.style.removeProperty('--bulk-bar-h');
      return;
    }
    const update = () => root.style.setProperty('--bulk-bar-h', `${Math.ceil(el.getBoundingClientRect().height)}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { ro.disconnect(); root.style.removeProperty('--bulk-bar-h'); };
  }, [visible, isMobile]);

  if (!visible) return null;

  const handleSpecial = async () => {
    const count = await setSpecial.mutateAsync({
      bottleIds: bottles.map(b => b.id),
      isSpecial: !allSpecial,
    });
    onNotice(`${count} ${count === 1 ? 'bottle' : 'bottles'} ${allSpecial ? 'removed from' : 'marked for'} special occasions`);
  };

  const handleWishlist = async () => {
    // One entry per wine+vintage, not per bottle.
    const seen = new Set<string>();
    const inputs = rows.flatMap(r => {
      const key = `${r.wine.id}__${r.vintage.year}`;
      if (seen.has(key)) return [];
      seen.add(key);
      return [{ wine_id: r.wine.id, vintage_year: r.vintage.year, priority: 'medium' as const }];
    });
    const created = await addToWishlist.mutateAsync(inputs);
    const skipped = inputs.length - created;
    onNotice(
      created === 0
        ? 'Already on the wishlist'
        : `${created} added to wishlist${skipped > 0 ? ` · ${skipped} already there` : ''}`
    );
  };

  const countLabel = (
    <span style={{ fontSize: 12.5, color: C.text, whiteSpace: 'nowrap' }}>
      <strong style={{ fontFamily: "'JetBrains Mono',monospace", color: C.gold, fontWeight: 600 }}>{rows.length}</strong>
      {' '}{rows.length === 1 ? 'group' : 'groups'}
      <span style={{ color: C.textMuted }}> &middot; </span>
      <strong style={{ fontFamily: "'JetBrains Mono',monospace", color: C.gold, fontWeight: 600 }}>{bottles.length}</strong>
      {' '}{bottles.length === 1 ? 'bottle' : 'bottles'}
    </span>
  );

  const buttons = (
    <>
      {isOwner && (
        <>
          <BarButton primary disabled={isBusy} onClick={onTransfer}>Transfer…</BarButton>
          <BarButton disabled={isBusy} onClick={() => { void handleSpecial(); }}>
            {setSpecial.isPending ? 'Saving…' : allSpecial ? 'Remove ★' : 'Mark ★'}
          </BarButton>
          <BarButton disabled={isBusy} onClick={() => { void handleWishlist(); }}>
            {addToWishlist.isPending ? 'Adding…' : 'Wishlist'}
          </BarButton>
        </>
      )}
      <BarButton disabled={isBusy} onClick={onExport}>Export CSV</BarButton>
      <BarButton disabled={isBusy} onClick={onClear}>Clear</BarButton>
    </>
  );

  const errorMessage = (setSpecial.isError || addToWishlist.isError)
    ? ((setSpecial.error ?? addToWishlist.error) as Error).message
    : null;

  if (isMobile) {
    // Full-width strip docked on the BottomNav: one line of status, one
    // horizontally scrollable row of buttons. It never wraps into a tall
    // floating card, and never sits over the nav.
    return (
      <div
        ref={barRef}
        role="region"
        aria-label="Bulk actions"
        style={{
          position: 'fixed',
          insetInline: 0,
          bottom: navHeight,
          zIndex: 40,
          background: C.surface,
          borderTop: `1px solid ${C.border}`,
          boxShadow: '0 -8px 24px rgba(0,0,0,0.45)',
          padding: '8px 0 10px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '0 16px 8px', minWidth: 0 }}>
          {countLabel}
          {(errorMessage || notice) && (
            <span style={{ fontSize: 11.5, color: errorMessage ? '#E07070' : C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
              {errorMessage ?? notice}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '0 16px', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
          {buttons}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        insetInline: 0,
        bottom: 24,
        zIndex: 40,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
        padding: '0 16px',
      }}
    >
      <div
        ref={barRef}
        role="region"
        aria-label="Bulk actions"
        style={{
          pointerEvents: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          maxWidth: '100%',
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 6,
          padding: '12px 18px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.55)',
        }}
      >
        {countLabel}

        <span style={{ width: 1, height: 20, background: C.border, flexShrink: 0 }} />

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {buttons}
        </div>

        {notice && (
          <span style={{ fontSize: 12, color: C.textMuted, whiteSpace: 'nowrap' }}>{notice}</span>
        )}
        {errorMessage && (
          <span style={{ fontSize: 12, color: '#E07070', whiteSpace: 'nowrap' }}>{errorMessage}</span>
        )}
      </div>
    </div>
  );
}

function BarButton({ children, onClick, disabled, primary }: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        background: primary ? C.gold : 'transparent',
        border: `1px solid ${primary ? C.gold : C.border}`,
        borderRadius: 4,
        padding: '7px 14px',
        fontSize: 12.5,
        fontWeight: primary ? 600 : 500,
        color: primary ? '#141821' : C.textDim,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}
